// src/prototype/components/sep/SEPSectionExecutionStrategy.tsx

import React from 'react';
import { Target, GitBranch, Users, ShieldCheck } from 'lucide-react';
import { isQuickWin, effortTierForBadge } from '../../config/opportunityBadge';

interface Finalist {
  id?: string;
  implementationEffort: 'Low' | 'Medium' | 'High';
  category: string;
}

export interface SEPSectionExecutionStrategyProps {
  clientName: string;
  finalists: Finalist[];
}

const PillarCard: React.FC<{ icon: React.ReactNode; title: string; description: string }> = ({ icon, title, description }) => (
  <div className="flex gap-3 p-4 bg-[#F5F7FA] rounded-xl border border-[#e5e7eb]">
    <div className="w-8 h-8 rounded-lg bg-[#18365E]/10 flex items-center justify-center shrink-0 mt-0.5">{icon}</div>
    <div>
      <p className="text-sm font-semibold text-[#18365E] mb-1">{title}</p>
      <p className="text-xs text-[#717182] leading-relaxed">{description}</p>
    </div>
  </div>
);

export const SEPSectionExecutionStrategy: React.FC<SEPSectionExecutionStrategyProps> = ({ clientName, finalists }) => {
  // Quick Win count and complexity tier both derive from the root, not from stored effort.
  const quickWins = finalists.filter(f => isQuickWin({ id: f.id, implementationEffort: f.implementationEffort })).length;
  const complexCount = finalists.filter(f => effortTierForBadge({ id: f.id, implementationEffort: f.implementationEffort }) === 'High').length;
  const categories = [...new Set(finalists.map(f => f.category))];

  return (
    <div className="bg-white border border-[#e5e7eb] rounded-xl p-6 mb-6">
      <div className="flex items-start gap-3 mb-6">
        <div className="w-[3px] self-stretch rounded-full bg-[#FF6E4C] shrink-0 min-h-[40px]" />
        <div>
          <p className="text-[10px] font-bold text-[#FF6E4C] uppercase tracking-widest mb-0.5">Section 2</p>
          <h2 className="text-lg font-bold text-[#18365E] leading-tight">Execution Approach</h2>
          <p className="text-[11px] text-[#717182] mt-0.5">Portfolio delivery approach, sequencing philosophy, and organizational alignment</p>
        </div>
      </div>

      <p className="text-sm text-[#18365E]/80 leading-relaxed mb-6">
        The execution strategy for <strong>{clientName}</strong> employs a phased delivery model designed to maximize early value realization while building organizational capability for more complex initiatives. Quick wins are sequenced first to demonstrate impact and build momentum, followed by structured rollouts of higher-complexity programs.
      </p>

      <div className="mb-6">
        <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-3">Execution Approach Pillars</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <PillarCard
            icon={<Target className="w-4 h-4 text-[#18365E]" />}
            title="Value-First Sequencing"
            description={quickWins > 0 ? `${quickWins} Quick Win initiative${quickWins > 1 ? 's are' : ' is'} prioritized for immediate launch to generate early ROI and executive confidence.` : 'Each initiative is sequenced to maximize value delivery at the earliest feasible milestone.'}
          />
          <PillarCard
            icon={<GitBranch className="w-4 h-4 text-[#18365E]" />}
            title="Phased Delivery Model"
            description={complexCount > 0 ? 'Complex initiatives are broken into structured phases, reducing risk and enabling course-correction before full commitment.' : 'All initiatives follow a structured phase gate process to ensure delivery confidence at each milestone.'}
          />
          <PillarCard
            icon={<Users className="w-4 h-4 text-[#18365E]" />}
            title="Cross-Functional Alignment"
            description={`Initiatives span ${categories.slice(0, 2).join(' and ')}${categories.length > 2 ? ` and ${categories.length - 2} additional domain${categories.length - 2 > 1 ? 's' : ''}` : ''}, requiring coordinated leadership from Clinical, Operations, Finance, and IT.`}
          />
          <PillarCard
            icon={<ShieldCheck className="w-4 h-4 text-[#18365E]" />}
            title="Governance-Integrated Execution"
            description="Each initiative includes defined approval gates, executive sponsors, and escalation pathways to ensure alignment remains visible to leadership throughout delivery."
          />
        </div>
      </div>

      <div className="bg-[#18365E]/[0.04] rounded-xl p-4 border border-[#18365E]/10">
        <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-2">Strategic Sequencing Rationale</p>
        <p className="text-sm text-[#18365E]/80 leading-relaxed">
          The portfolio is sequenced to build a pattern of delivery success. Early Quick Wins generate tangible ROI within the first two quarters, creating organizational credibility for subsequent higher-investment initiatives. Moderate-complexity programs follow once baseline infrastructure and cross-functional workflows are established. Complex, long-horizon programs are structured as phased pilots to de-risk full commitment.
        </p>
      </div>
    </div>
  );
};

export default SEPSectionExecutionStrategy;